/**
 * Analyzer: Process vs Outcome Mismatch
 *
 * Flags sessions where the user's self-rating of process quality
 * doesn't match the financial outcome. This is one of the most valuable
 * signals for a trader — it separates variance from bad habits.
 *
 *   Good process + losing result  → variance, don't change what worked
 *   Bad  process + winning result → don't reinforce sloppy habits
 *
 * Requires a session rating ('good', 'okay', 'bad').
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { fmt$ } from '../utils';

export const key = 'processMismatch';

/**
 * @param {Object} ctx - { session, enriched, summary }
 * @returns {Observation|null}
 */
export const analyze = (ctx) => {
  const { session, summary } = ctx;
  if (!session || !session.rating) return null;
  const { total } = summary;

  // Good process, losing outcome → variance signal
  if (session.rating === 'good' && total < 0) {
    return makeObservation({
      id: `${key}:${session.id}:good-bad`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.INFO,
      category: CATEGORY.VARIANCE,
      title: `Good process, ${fmt$(total)} outcome — likely variance`,
      detail: `You rated this session as good but finished ${fmt$(total)}. Execution was likely sound; the setup may have been the wrong fit for today's tape.`,
      action: 'Don\'t change what worked — but note what tape conditions punished this setup.',
      evidence: { rating: session.rating, total },
      metric: { label: 'Net', value: total, unit: '$' },
      confidence: 0.8,
      priority: 72
    });
  }

  // Bad process, winning outcome → don't reinforce bad habits
  if (session.rating === 'bad' && total > 0) {
    return makeObservation({
      id: `${key}:${session.id}:bad-good`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.WARNING,
      category: CATEGORY.DISCIPLINE,
      title: `${fmt$(total)} despite poor process — don't reinforce it`,
      detail: `Net positive (${fmt$(total)}) but you flagged the process as bad. Results can bail you out of bad habits; the market will collect eventually.`,
      action: 'Write the specific rule you broke in Lessons — that\'s the real output of this session.',
      evidence: { rating: session.rating, total },
      metric: { label: 'Net', value: total, unit: '$' },
      confidence: 0.85,
      priority: 85
    });
  }

  return null;
};
